import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Globe } from 'lucide-react';
import Swal from 'sweetalert2';
import TiptapEditor from './TiptapEditor';
import ImageUploader from './ImageUploader';
import { createArticle, updateArticle, getCategories } from '../../../services/newsNewsletterApi';

const TUNISIAN_ENERGY_DOMAINS = [
  { key: 'SOLAR_PV', label: 'Solaire Photovoltaïque' },
  { key: 'SOLAR_THERMAL', label: 'Solaire Thermique' },
  { key: 'WIND', label: 'Éolien' },
  { key: 'BIOMASS', label: 'Biomasse' },
  { key: 'ENERGY_EFFICIENCY', label: 'Efficacité Énergétique' },
  { key: 'REGULATION_STEG', label: 'Réglementation STEG' },
  { key: 'FISCAL_INCENTIVES', label: 'Incitations Fiscales' },
  { key: 'CARBON_FOOTPRINT', label: 'Bilan Carbone' },
  { key: 'HYDROGEN_GREEN', label: 'Hydrogène Vert' },
  { key: 'GENERAL_ENERGY', label: 'Énergie Générale' },
];

const REGULATORY_STAGES = [
  { key: 'DRAFT_PROPOSAL', label: 'Proposition / Projet en consultation' },
  { key: 'PARLIAMENT_BILL', label: 'Projet de Loi en débat parlementaire (ARP)' },
  { key: 'LAW_PASSED', label: 'Loi adoptée par l’ARP' },
  { key: 'DECREE_PUBLISHED', label: 'Décret d’application publié au JORT' },
  { key: 'IN_FORCE_ACTIVE', label: 'En vigueur & applicable en Tunisie' },
  { key: 'AMENDED_SUPERSEDED', label: 'Modifié ou remplacé par un nouveau texte' },
  { key: 'REPEALED_EXPIRED', label: 'Abrogé ou expiré' },
];

export default function ArticleEditModal({
  isOpen,
  onClose,
  article = null,
  onSaved,
}) {
  const [categories, setCategories] = useState([]);
  const [langTab, setLangTab] = useState('fr'); // 'fr' | 'ar' | 'en'
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    contentHtml: '',
    primaryCategoryId: '',
    tunisianDomains: ['SOLAR_PV'],
    coverImage: null,
    tunisianContext: {
      regulatoryStage: 'IN_FORCE_ACTIVE',
      officialJournalReferences: '',
      effectiveDate: '',
    },
    translations: {
      ar: { title: '', excerpt: '', contentHtml: '' },
      en: { title: '', excerpt: '', contentHtml: '' },
    },
  });

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      if (article) {
        setFormData({
          title: article.title || '',
          excerpt: article.excerpt || '',
          contentHtml: article.contentHtml || '',
          primaryCategoryId: article.primaryCategoryId?._id || article.primaryCategoryId || '',
          tunisianDomains: article.tunisianDomains?.length ? article.tunisianDomains : ['SOLAR_PV'],
          coverImage: article.coverImage || null,
          tunisianContext: {
            regulatoryStage: article.tunisianContext?.regulatoryStage || 'IN_FORCE_ACTIVE',
            officialJournalReferences: article.tunisianContext?.officialJournalReferences || '',
            effectiveDate: article.tunisianContext?.effectiveDate ? article.tunisianContext.effectiveDate.split('T')[0] : '',
          },
          translations: {
            ar: {
              title: article.translations?.ar?.title || '',
              excerpt: article.translations?.ar?.excerpt || '',
              contentHtml: article.translations?.ar?.contentHtml || '',
            },
            en: {
              title: article.translations?.en?.title || '',
              excerpt: article.translations?.en?.excerpt || '',
              contentHtml: article.translations?.en?.contentHtml || '',
            },
          },
        });
      } else {
        // Reset form for new article
        setFormData({
          title: '',
          excerpt: '',
          contentHtml: '',
          primaryCategoryId: '',
          tunisianDomains: ['SOLAR_PV'],
          coverImage: null,
          tunisianContext: {
            regulatoryStage: 'IN_FORCE_ACTIVE',
            officialJournalReferences: '',
            effectiveDate: '',
          },
          translations: {
            ar: { title: '', excerpt: '', contentHtml: '' },
            en: { title: '', excerpt: '', contentHtml: '' },
          },
        });
      }
    }
  }, [isOpen, article]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      if (res?.success) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  if (!isOpen) return null;

  const toggleDomain = (domainKey) => {
    setFormData((prev) => {
      const exists = prev.tunisianDomains.includes(domainKey);
      const next = exists
        ? prev.tunisianDomains.filter((d) => d !== domainKey)
        : [...prev.tunisianDomains, domainKey];
      return { ...prev, tunisianDomains: next.length > 0 ? next : ['SOLAR_PV'] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || formData.title.trim().length < 5) {
      Swal.fire('Validation', 'Le titre de l’article doit comporter au moins 5 caractères.', 'warning');
      return;
    }

    if (!formData.primaryCategoryId) {
      Swal.fire('Validation', 'Veuillez sélectionner une catégorie principale.', 'warning');
      return;
    }

    if (!formData.tunisianDomains.length) {
      Swal.fire('Validation', 'Veuillez sélectionner au moins un domaine énergétique tunisien.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        primaryCategoryId: formData.primaryCategoryId,
      };

      if (article?._id) {
        await updateArticle(article._id, payload);
        Swal.fire('Succès', 'Article modifié avec succès.', 'success');
      } else {
        await createArticle(payload);
        Swal.fire('Succès', 'Nouvel article créé en brouillon (DRAFT).', 'success');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      Swal.fire('Erreur', err.message || 'Impossible de sauvegarder l’article', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
      <div className="an-modal-card an-modal-xlarge" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="an-modal-header">
          <div className="an-modal-title">
            <Save size={20} className="text-primary mr-2" />
            <span>{article ? 'Modifier l’article' : 'Rédiger un nouvel article'}</span>
          </div>
          <button className="an-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="an-modal-body an-modal-scrollable">
            {/* Lang switcher */}
            <div className="an-lang-switcher-bar">
              <span className="an-lang-label">
                <Globe size={15} className="mr-1" />
                Langue de rédaction :
              </span>
              <button
                type="button"
                className={`an-lang-btn ${langTab === 'fr' ? 'active' : ''}`}
                onClick={() => setLangTab('fr')}
              >
                Français (Principal)
              </button>
              <button
                type="button"
                className={`an-lang-btn ${langTab === 'ar' ? 'active' : ''}`}
                onClick={() => setLangTab('ar')}
              >
                العربية (Arabe)
              </button>
              <button
                type="button"
                className={`an-lang-btn ${langTab === 'en' ? 'active' : ''}`}
                onClick={() => setLangTab('en')}
              >
                English (Anglais)
              </button>
            </div>

            {/* TAB FR (Default / Main) */}
            {langTab === 'fr' && (
              <div className="an-editor-fields animate-fade-in">
                <div className="an-form-group">
                  <label>Titre de l'article <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="an-input an-input-lg"
                    placeholder="Ex: Nouvelle tarification STEG et prime Prosol 2026 en Tunisie"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="an-form-row">
                  <div className="an-form-group flex-1">
                    <label>Catégorie principale <span className="text-danger">*</span></label>
                    <select
                      className="an-select"
                      value={formData.primaryCategoryId}
                      onChange={(e) => setFormData({ ...formData, primaryCategoryId: e.target.value })}
                      required
                    >
                      <option value="">-- Choisir une catégorie --</option>
                      {categories.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="an-form-group flex-1">
                    <label>Stade réglementaire tunisien</label>
                    <select
                      className="an-select"
                      value={formData.tunisianContext.regulatoryStage}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tunisianContext: {
                            ...formData.tunisianContext,
                            regulatoryStage: e.target.value,
                          },
                        })
                      }
                    >
                      {REGULATORY_STAGES.map((stg) => (
                        <option key={stg.key} value={stg.key}>
                          {stg.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="an-form-group">
                  <label>Domaines énergétiques tunisiens <span className="text-danger">*</span></label>
                  <div className="an-domain-chips">
                    {TUNISIAN_ENERGY_DOMAINS.map((domain) => {
                      const isSelected = formData.tunisianDomains.includes(domain.key);
                      return (
                        <button
                          key={domain.key}
                          type="button"
                          className={`an-domain-chip ${isSelected ? 'active' : ''}`}
                          onClick={() => toggleDomain(domain.key)}
                        >
                          {domain.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="an-form-row">
                  <div className="an-form-group flex-2">
                    <label>Références Journal Officiel (JORT) / Textes légaux</label>
                    <input
                      type="text"
                      className="an-input"
                      placeholder="Ex: JORT n° 14 du 30 janvier 2026 — Arrêté du ministre de l’Industrie"
                      value={formData.tunisianContext.officialJournalReferences}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tunisianContext: {
                            ...formData.tunisianContext,
                            officialJournalReferences: e.target.value,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="an-form-group flex-1">
                    <label>Date d'entrée en vigueur</label>
                    <input
                      type="date"
                      className="an-input"
                      value={formData.tunisianContext.effectiveDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tunisianContext: {
                            ...formData.tunisianContext,
                            effectiveDate: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="an-form-group">
                  <label>Image de couverture (Génération automatique des formats WebP)</label>
                  <ImageUploader
                    value={formData.coverImage}
                    onChange={(cover) => setFormData({ ...formData, coverImage: cover })}
                  />
                </div>

                <div className="an-form-group">
                  <label>Résumé / Chapeau éditorial</label>
                  <textarea
                    className="an-textarea"
                    rows={2}
                    placeholder="Synthèse claire en 1 ou 2 phrases pour le flux d'actualités et la newsletter..."
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  />
                </div>

                <div className="an-form-group">
                  <label>Contenu de l'article (Éditeur riche Tiptap)</label>
                  <TiptapEditor
                    content={formData.contentHtml}
                    onChange={(html) => setFormData({ ...formData, contentHtml: html })}
                  />
                </div>
              </div>
            )}

            {/* TAB AR (Arabic translation) */}
            {langTab === 'ar' && (
              <div className="an-editor-fields animate-fade-in" dir="rtl">
                <div className="an-form-group text-right">
                  <label>عنوان المقال بالعربية</label>
                  <input
                    type="text"
                    className="an-input an-input-lg text-right"
                    placeholder="عنوان المقال..."
                    value={formData.translations.ar.title}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        translations: {
                          ...formData.translations,
                          ar: { ...formData.translations.ar, title: e.target.value },
                        },
                      })
                    }
                  />
                </div>

                <div className="an-form-group text-right">
                  <label>الملخص بالعربية</label>
                  <textarea
                    className="an-textarea text-right"
                    rows={2}
                    placeholder="ملخص قصير للمقال..."
                    value={formData.translations.ar.excerpt}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        translations: {
                          ...formData.translations,
                          ar: { ...formData.translations.ar, excerpt: e.target.value },
                        },
                      })
                    }
                  />
                </div>

                <div className="an-form-group text-right">
                  <label>المحتوى التحريري الكامل (محرر Tiptap)</label>
                  <TiptapEditor
                    content={formData.translations.ar.contentHtml}
                    onChange={(html) =>
                      setFormData({
                        ...formData,
                        translations: {
                          ...formData.translations,
                          ar: { ...formData.translations.ar, contentHtml: html },
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}

            {/* TAB EN (English translation) */}
            {langTab === 'en' && (
              <div className="an-editor-fields animate-fade-in">
                <div className="an-form-group">
                  <label>Article Title (English)</label>
                  <input
                    type="text"
                    className="an-input an-input-lg"
                    placeholder="Article title in English..."
                    value={formData.translations.en.title}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        translations: {
                          ...formData.translations,
                          en: { ...formData.translations.en, title: e.target.value },
                        },
                      })
                    }
                  />
                </div>

                <div className="an-form-group">
                  <label>Summary / Excerpt (English)</label>
                  <textarea
                    className="an-textarea"
                    rows={2}
                    placeholder="Short summary in English..."
                    value={formData.translations.en.excerpt}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        translations: {
                          ...formData.translations,
                          en: { ...formData.translations.en, excerpt: e.target.value },
                        },
                      })
                    }
                  />
                </div>

                <div className="an-form-group">
                  <label>Full Content (Tiptap Editor)</label>
                  <TiptapEditor
                    content={formData.translations.en.contentHtml}
                    onChange={(html) =>
                      setFormData({
                        ...formData,
                        translations: {
                          ...formData.translations,
                          en: { ...formData.translations.en, contentHtml: html },
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="an-modal-footer">
            <button
              type="button"
              className="an-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="an-btn-primary"
              disabled={isSubmitting}
            >
              <Save size={16} />
              <span>{isSubmitting ? 'Enregistrement...' : 'Enregistrer l’article'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

